import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import type { LiveSessionAnalysis } from './types';

const ANALYSIS_POLL_MS = 2000;

type RunFailure = 'failed' | 'no_events' | 'rate_limited';
type RunStatus = RunFailure | 'idle' | 'running';

const RUN_ERRORS: Record<RunFailure, string> = {
  failed: 'The summary failed. Please try again.',
  no_events: 'This session has no event log to summarize.',
  rate_limited: "You have reached today's AI limit. Try again tomorrow.",
};

const analysisKey = (sessionId: string) => ['LiveSessionForensics', 'analysis', sessionId];

const failureFor = (status: string): RunFailure => (
  status === 'rate_limited' || status === 'no_events' ? status : 'failed'
);

export const useSessionAnalysis = (sessionId: string, enabled: boolean) => useQuery({
  enabled,
  queryFn: () => axios
    .get<{ data: LiveSessionAnalysis | null }>(Routes.plato_api_analysis_sysadmin_live_session_path(sessionId))
    .then(res => res.data.data),
  queryKey: analysisKey(sessionId),
});

// POST enqueues a worker and returns a job id; the job status endpoint is polled until it settles,
// then the stored analysis is refetched. Same shape as the Content Audit tool.
export const useRunAnalysis = (sessionId: string) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<RunStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number>();

  const stopPolling = () => {
    window.clearInterval(timerRef.current);
    timerRef.current = undefined;
  };

  useEffect(() => stopPolling, []);

  const fail = (failure: RunFailure, message?: string | null) => {
    stopPolling();
    setStatus(failure);
    setError(message || RUN_ERRORS[failure]);
  };

  const pollJob = (jobId: string) => {
    timerRef.current = window.setInterval(async () => {
      try {
        const { data } = await axios.get(Routes.plato_api_job_status_path(jobId));
        const jobStatus: string | undefined = data.data?.status;
        if (jobStatus === 'completed') {
          stopPolling();
          await queryClient.invalidateQueries({ queryKey: analysisKey(sessionId) });
          setStatus('idle');
        } else if (jobStatus === 'rate_limited') {
          fail('rate_limited');
        } else if (jobStatus === 'failed' || jobStatus === 'not_found') {
          fail('failed', data.data?.error?.message);
        }
      } catch {
        fail('failed');
      }
    }, ANALYSIS_POLL_MS);
  };

  const run = async () => {
    setStatus('running');
    setError(null);
    try {
      const { data } = await axios.post(Routes.plato_api_analysis_sysadmin_live_session_path(sessionId));
      if (data.status === 'enqueued') {
        pollJob(data.job_id);
      } else {
        fail(failureFor(data.status));
      }
    } catch {
      fail('failed');
    }
  };

  return { error, isRunning: status === 'running', run };
};
